import * as d3 from 'd3'

import { calcData } from '@aytm/app/stats/page/question-views/cjc/components/chart/lib/chart-utils'
import { calcBestPackage, calcMedianPackage, calcWorstPackage } from '@aytm/app/stats/page/question-views/cjc/lib/calc'

// SVG export for the rich D3 conjoint chart. Ported from the legacy Backbone
// ChartView so the live-page PPTX path (pptx.coffee -> block.formatSVG /
// formatAllSVG) keeps working now that the chart is drawn by React (lib/chart.js).
// drawChart emits the same group class structure these functions select on, and
// calcData returns the same state shape they read, so the D3 repositioning is
// applied to the React-rendered <svg>. block.coffee keeps only the final AYTM
// serialization (page.lib.formatSVG); everything here is state -> positioned SVG.

// Column widths / x-positions come from the CURRENT chart state (`state`); the
// batches, percent ticks and point marker are stretched to the full width.
export const resizeSVG = (svg, state, settings) => {
  const w = state.bs.reduce((acc, cur) => acc + cur.cw, settings.pw)

  svg.attr('width', w).attr('height', settings.height).attr('viewbox', `0 0 ${w} ${settings.height}`)
  svg.select('rect').attr('width', w)

  svg.selectAll('.group-percent').each(function () {
    d3.select(this)
      .selectAll('line')
      .filter((d, i) => i === 1)
      .attr('x2', w)
  })
  svg
    .selectAll('.group-point')
    .select('rect')
    .attr('width', w - settings.pw)

  svg.selectAll('.group-batch').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index'), 10)
    const d = state.bs.find(b => b.id === i)
    g.attr('transform', `translate(${d.cx},0)`)
    g.select('rect').attr('width', d.cw)
  })
  svg.selectAll('.group-batch-wheel-hidden').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const d = state.bs.find(b => b.id === i).wheel[0]
    g.select('path').attr(
      'd',
      `M0,${d.d - settings.bp}L${d.cw - 3},${d.d}L${d.cw - 3},${d.d + d.h}L0,${d.d + d.h + settings.bp}`
    )
    g.select('text').attr('x', (d.cw - 3) / 2)
  })
  svg.selectAll('.group-batch-wheel-visible').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const d = state.bs.find(b => b.id === i).wheel[0]
    g.select('rect').attr('width', d.cw - settings.bp)
  })
  svg.selectAll('.group-batch-av').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const j = parseInt(g.attr('index').split('-')[1], 10)
    const d = state.bs.find(b => b.id === i).wheel[0].avs.find(av => av.b === i && av.id === j)
    g.select('rect').attr('width', d.cw - settings.bp)
    g.select('text').text(d.t)
  })
  svg.selectAll('.group-batch-header').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index'), 10)
    const d = state.bs.find(b => b.id === i)
    g.attr('transform', `translate(${d.cx},0)`)
    g.select('rect').attr('width', d.cw)
    g.selectAll('line')
      .filter((d2, k) => k === 1)
      .attr('x2', d.cw)
    g.select('text').text(d.v ? `${d.id}. ${d.t}` : '')
  })

  return svg
}

// Re-positions the y-axis elements (point/scale/percents and each batch wheel) to
// a TARGET state -- used to render a package/persona other than the one on screen.
export const restateSVG = (svg, state, settings) => {
  svg
    .select('.group-point')
    .attr('transform', `translate(0,${state.ps.point[0].y - settings.pp})`)
    .select('text')
    .select('tspan')
    .text(state.ps.point[0].t)
  svg.select('.group-scale').attr('transform', `translate(0,${state.ps.scale[0].y - settings.pp})`)
  svg.selectAll('.group-percent').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index'), 10)
    const d = state.ps.scale[0].points.find(p => p.id === i)
    g.attr('transform', `translate(0,${d.y - settings.pp})`).style('opacity', d.v === 'visible' ? 1 : 0)
  })

  svg.selectAll('.group-batch-wheel.group-batch-wheel-hidden').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const d = state.bs.find(b => b.id === i).wheel[0]
    g.attr('transform', `translate(0,${d.y})`)
    g.select('path').attr(
      'd',
      `M0,${d.d - settings.bp}L${d.w - 3},${d.d}L${d.w - 3},${d.d + d.h}L0,${d.d + d.h + settings.bp}`
    )
    g.select('text').attr('y', d.avs.find(v => v.s).y + 6)
  })

  svg.selectAll('.group-batch-wheel.group-batch-wheel-visible').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const d = state.bs.find(b => b.id === i).wheel[0]
    g.attr('transform', `translate(0,${d.y})`)
    g.select('path').attr(
      'd',
      `M0,${d.d - settings.bp}L${settings.bp},${d.d}L${settings.bp},${d.h + d.d}L0,${d.h + d.d + settings.bp}`
    )
    g.select('rect')
      .attr('y', d.h === settings.sh ? d.d : d.r[0])
      .attr('height', d.h === settings.sh ? d.h : d.r[1])
  })
  svg.selectAll('.group-batch-av-pointer').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const j = parseInt(g.attr('index').split('-')[1], 10)
    const d = state.bs.find(b => b.id === i).wheel[0].avs.find(av => av.b === i && av.id === j)
    g.attr('transform', `translate(${settings.bp},${d.y})`)
  })
  svg.selectAll('.group-batch-av').each(function () {
    const g = d3.select(this)
    const i = parseInt(g.attr('index').split('-')[0], 10)
    const j = parseInt(g.attr('index').split('-')[1], 10)
    const d = state.bs.find(b => b.id === i).wheel[0].avs.find(av => av.b === i && av.id === j)
    g.attr('transform', `translate(${settings.bp},${d.y + d.d})`)
    g.style('opacity', d.v === 'visible' ? 1 : 0)
    g.select('rect')
      .attr('x', 0)
      .attr('y', -d.h / 2)
      .attr('height', d.h)
    g.select('text')
      .attr('y', d.s ? 6 : 4)
      .attr('fill', d.s ? '#ffffff' : '#000000')
      .attr('font-size', d.s ? settings.fs2 : settings.fs1)
  })

  return svg
}

// Clone the live <svg>, drop interactive-only groups, optionally restate to a
// target package/persona, then resize to the current column layout. Returns the
// prepared node (block.coffee applies page.lib.formatSVG). `currentState` drives
// widths; `targetState` (when given) drives the y positions.
export const formatSVG = (svgNode, currentState, settings, targetState = null) => {
  if (!svgNode || !currentState) return null

  const svg = d3.select(svgNode.cloneNode(true))

  svg.selectAll('g.download').remove()
  svg.selectAll('g.group-shadow').remove()
  svg.selectAll('g.group-sidebar').remove()
  svg.selectAll('g.group-batch-popup').remove()

  if (targetState) restateSVG(svg, targetState, settings)
  resizeSVG(svg, currentState, settings)

  return svg.node()
}

// PPTX multi-output: the current view first, then one node per cluster rendered at
// the requested package (best/median/worst, else the current package). Empty
// clusters yield null. Only the requested package is computed -- no coffee Conjoint.Lib.
export const formatAllSVG = ({
  svgNode,
  currentState,
  answer,
  batches,
  mode,
  currentPackage,
  hiddenBatches,
  sortOrder,
  center,
  scale,
  colorTheme,
  chartLegendMode,
  packageType,
  settings
}) => {
  const results = [formatSVG(svgNode, currentState, settings)]

  // Per-call cache so each cluster's calcData memoizes independently of the live chart.
  const cachedStat = {}
  const clusters = answer?.clusters || []

  clusters.forEach((cluster, i) => {
    if (!svgNode || !cluster?.amount) {
      results.push(null)
      return
    }

    const persona = i + 1

    let pkg
    switch (packageType) {
      case 'best':
        pkg = calcBestPackage(mode, answer, persona)
        break
      case 'median':
        pkg = calcMedianPackage(mode, answer, persona)
        break
      case 'worst':
        pkg = calcWorstPackage(mode, answer, persona)
        break
      default:
        pkg = currentPackage
    }

    const state = calcData({
      answer,
      batches,
      mode,
      persona,
      currentPackage: pkg,
      hiddenBatches,
      sortOrder,
      center,
      scale,
      colorTheme,
      chartLegendMode,
      settings,
      cachedStat,
      prevStateId: '',
      onPrevStateIdChange: () => {}
    })

    results.push(formatSVG(svgNode, currentState, settings, state))
  })

  return results
}
